import { Grid, makeStyles, Typography } from "@material-ui/core"
import { Pagination, PaginationItem, PaginationRenderItemParams } from "@material-ui/lab"
import { PageProps } from "gatsby"
import React from "react"
import { Helmet } from "react-helmet"

import { Layout } from "../components/Layout"
import { Link } from "../components/Link"
import { SearchResults } from "../components/SearchResults"
import { SeoContent } from "../components/SeoContent"
import { CollectionPageContext } from "../interfaces/pageContext"

const useStyles = makeStyles((theme) => ({
  heading: {
    textAlign: "center",
  },
  inline: {
    display: "inline",
  },
  container: {
    "& hr": {
      borderBottom: "1px solid #ededed",
    },
  },
  searchResults: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
  },
  navList: {
    listStyleType: "none",
    padding: 0,
    margin: 0,
  },
  navListItem: {
    fontStyle: "italic",
    marginBottom: theme.spacing(1),
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    padding: `${theme.spacing(2)}px 0 !important`,
  },
}))

export interface CollectionPageProps extends PageProps<any, CollectionPageContext> {
  name: string
  description: string
  primaryKeyword: string
  secondaryKeyword: string
  seoContent?: { type: string; value: string }[]
  navigation?: { heading: string; links: { to: string; text: string }[] }[]
}

const CollectionPage: React.FC<CollectionPageProps> = (props) => {
  const classes = useStyles(props)
  const { data, location, pageContext, name, primaryKeyword, seoContent, navigation } = props
  const { edges: products, totalCount } = data.allProductsCsv
  const {
    humanPageNumber,
    limit,
    nextPagePath,
    numberOfPages,
    pageNumber,
    previousPagePath,
  } = pageContext

  let { description, secondaryKeyword } = props
  if (humanPageNumber > 1) {
    const start = pageNumber * limit + 1
    const stop = Math.min((pageNumber + 1) * limit, totalCount)
    description = `${start}-${stop} (von ${totalCount}) ${description}`
    secondaryKeyword = `Seite ${humanPageNumber}`
    const { breadcrumb } = pageContext
    const { crumbs } = breadcrumb
    crumbs[crumbs.length - 1].crumbLabel = `Seite ${humanPageNumber}`
  }
  pageContext.title = `${primaryKeyword} - ${secondaryKeyword}`

  const { pathname } = location

  const renderItem = (params: PaginationRenderItemParams) => {
    const { page } = params
    if (page > 0 && page <= numberOfPages) {
      const basepath = pathname.replace(/\/\d+\/$/g, `/`)
      const href = `${basepath}${page === 1 ? `` : `${page}/`}`
      // HACK: We have to cast because the PaginationItemProps are incompatible with the LinkProps.
      return <PaginationItem component={Link as any} href={href} underline="none" {...params} />
    }
    return <PaginationItem {...params} /> // Do not render a link to pages that don't exist.
  }

  return (
    <Layout location={location} pageContext={pageContext}>
      <Helmet defer={false}>
        <meta name="description" content={description} />
        {previousPagePath && <link rel="prev" href={previousPagePath} />}
        {nextPagePath && <link rel="next" href={nextPagePath} />}
      </Helmet>
      <Grid container={true} spacing={4} alignItems="stretch" direction="column" wrap="nowrap">
        <Grid item={true} className={classes.heading}>
          <div>
            <Typography variant="h1" align="center" className={classes.inline}>
              {name}
            </Typography>
            <Typography component="span" variant="h1" align="center">
              {" "}
              ({totalCount})
            </Typography>
          </div>
        </Grid>
        <Grid item={true}>
          <SearchResults
            searchQuery={name}
            results={products.map(({ node }: any) => node.fields)}
          />
        </Grid>
        <Grid item={true} className={classes.pagination}>
          <Pagination page={humanPageNumber} count={numberOfPages} renderItem={renderItem} />
        </Grid>
        {/* Only show the SEO content on the first page. */}
        {humanPageNumber === 1 && seoContent && (
          <Grid item={true}>
            <SeoContent blocks={seoContent} />
          </Grid>
        )}
        {/* Show the navigation on every page. */}
        {navigation &&
          navigation.map(({ heading, links }, navKey) => (
            <Grid item={true} key={navKey}>
              <Typography align="center" variant="h2">
                {heading}
              </Typography>
              <nav>
                <Grid container={true} spacing={1} component="ul" className={classes.navList}>
                  {links.map(({ to, text }, linkKey) => (
                    <Grid
                      item={true}
                      xs={6}
                      sm={4}
                      md={3}
                      component="li"
                      key={linkKey}
                      className={classes.navListItem}
                    >
                      <Link href={to}>{text}</Link>
                    </Grid>
                  ))}
                </Grid>
              </nav>
            </Grid>
          ))}
      </Grid>
    </Layout>
  )
}

export default CollectionPage
